import { useEffect, useState, useRef } from 'react';
import type { Toast as ToastType } from '@/shared/contexts';
import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';

interface ToastProps {
  toast: ToastType;
  onRemove: () => void;
}

const Toast = ({ toast, onRemove }: ToastProps) => {
  const [visible, setVisible] = useState(true);
  const progressRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const toastDuration = toast.duration;
  const animationDuration = 300; // Exit animation duration in ms

  // Start exit animation before removing from DOM
  useEffect(() => {
    // Initialize start time when effect runs
    startTimeRef.current = Date.now();
    const visibleDuration = toastDuration - animationDuration;

    const timer = setTimeout(() => {
      setVisible(false);
    }, visibleDuration);

    // Update progress bar - this will run for the full visible duration
    const updateProgressBar = () => {
      if (!progressRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      // Make sure progress bar finishes exactly when the visible duration ends
      const remaining = Math.max(0, visibleDuration - elapsed);
      const progress = (remaining / visibleDuration) * 100;

      progressRef.current.style.width = `${progress}%`;

      if (elapsed < visibleDuration) {
        animationFrameRef.current = requestAnimationFrame(updateProgressBar);
      } else if (progressRef.current) {
        // Ensure we set to exactly 0% at the end
        progressRef.current.style.width = '0%';
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateProgressBar);

    return () => {
      clearTimeout(timer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [toastDuration]);

  // Once exit animation completes, call onRemove
  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(onRemove, animationDuration);
      return () => clearTimeout(timer);
    }
    return () => {}; // Return empty cleanup function when visible is true
  }, [visible, onRemove, animationDuration]);

  // Get the appropriate icon and background color based on toast type
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: <Check className="w-5 h-5" />,
          bgColor: 'bg-accent-success text-text-inverse',
          progressColor: 'bg-text-inverse/60'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-5 h-5" />,
          bgColor: 'bg-accent-error text-text-inverse',
          progressColor: 'bg-text-inverse/60'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          bgColor: 'bg-accent-warning text-text-inverse',
          progressColor: 'bg-text-inverse/60'
        };
      case 'info':
      default:
        return {
          icon: <Info className="w-5 h-5" />,
          bgColor: 'bg-accent-info text-text-inverse',
          progressColor: 'bg-text-inverse/60'
        };
    }
  };

  const { icon, bgColor, progressColor } = getToastStyles();

  return (
    <div
      className={`${bgColor} flex w-full max-w-md flex-col overflow-hidden rounded-lg shadow-lg transition-[opacity,transform] duration-200 md:mx-0 ${
        visible
          ? 'translate-y-0 scale-100 opacity-100'
          : '-translate-y-2 scale-95 opacity-0'
      }`}
      style={{
        minWidth: '280px',
        maxWidth: '100%'
      }}
      role="alert"
    >
      <div className="flex items-center gap-2 p-3 md:gap-3 md:p-3">
        <div className="shrink-0">{icon}</div>
        <p className="wrap-break-words flex-1 text-sm font-medium md:text-sm">
          {toast.message}
        </p>
      </div>

      {/* Progress indicator */}
      <div className="h-1 w-full bg-black/10">
        <div
          ref={progressRef}
          className={`h-full ${progressColor}`}
          style={{ width: '100%', transition: 'width linear' }}
        />
      </div>
    </div>
  );
};

export default Toast;
